import { useFiltrosGlobales } from '@/lib/FiltrosGlobalesContext';
import { type PartidaSel, cliente } from '@/lib/cliente';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

export function useAvanceMatriz() {
  const { filtros } = useFiltrosGlobales();
  return useQuery({
    queryKey: ['avance-matriz', filtros],
    queryFn: () => cliente.avance.matriz(filtros),
  });
}

/**
 * Versión SEMANAL de la matriz de avance (desglose Año › Mes › Semana). El
 * tiempo lo controla el selector PROPIO de la pantalla (semanasSel = claves
 * "anio-mes-semanaIso"), NO el filtro global. Los filtros estructurales
 * (proyecto/obra/área/etc.) sí vienen del filtro global. Se dispara solo cuando
 * hay ≥1 semana seleccionada.
 */
export function useAvanceMatrizSemanal(semanasSel: string[]) {
  const { filtros } = useFiltrosGlobales();
  return useQuery({
    queryKey: ['avance-matriz-semanal', filtros, semanasSel],
    queryFn: () => cliente.avance.matriz(filtros, 'semana', semanasSel),
    enabled: semanasSel.length > 0,
  });
}

/** Períodos semanales disponibles (anio, mes, semanaIso). Para el selector
 *  propio Año › Mes › Semana ISO. No depende de filtros. */
export function usePeriodosSemanales() {
  return useQuery({
    queryKey: ['periodos-semanales'],
    queryFn: () => cliente.periodos.semanales(),
    staleTime: 1000 * 60 * 30,
  });
}

/**
 * KPIs por obra. Si se pasa un corte {anio, mes}, recalcula a esa fecha; si no,
 * devuelve el estado actual (snapshot fact_kpis_obra).
 */
export function useKpisObras() {
  const { filtros } = useFiltrosGlobales();
  // Corte = mes máximo del filtro Períodos global (igual que Productividad).
  // Si no hay período seleccionado → estado actual (snapshot).
  const corte = corteDesdeFiltros(filtros);
  return useQuery({
    queryKey: ['kpis-obras', filtros, corte ?? 'actual'],
    queryFn: () =>
      corte
        ? cliente.avance.kpisObrasCorte(filtros, corte.anio, corte.mes)
        : cliente.avance.kpisObras(filtros),
  });
}

/** Drill-down: KPIs por partida de una obra. Solo fetch cuando worksNo != null. */
export function useKpisPartidas(worksNo: string | null) {
  return useQuery({
    queryKey: ['kpis-partidas', worksNo],
    queryFn: () => cliente.avance.kpisPartidas(worksNo!),
    enabled: !!worksNo,
    staleTime: 60_000,
  });
}

/** Drill-down: avance por partida × mes de una obra. */
export function useAvancePartidas(worksNo: string | null) {
  return useQuery({
    queryKey: ['avance-partidas', worksNo],
    queryFn: () => cliente.avance.avancePartidas(worksNo!),
    enabled: !!worksNo,
    staleTime: 60_000,
  });
}

/** Pantalla Mensual: producción acumulada + indirecto del mes por obra. */
export function useMensualObras() {
  const { filtros } = useFiltrosGlobales();
  return useQuery({
    queryKey: ['mensual-obras', filtros],
    queryFn: () => cliente.avance.mensualObras(filtros),
  });
}

/** Reporte Vivienda Gerencia (viviendas VB/VC/VN). Respeta los filtros globales. */
export function useViviendaGerencia() {
  const { filtros } = useFiltrosGlobales();
  return useQuery({
    queryKey: ['vivienda-gerencia', filtros],
    queryFn: () => cliente.avance.viviendaGerencia(filtros),
    placeholderData: keepPreviousData,
  });
}

/** Informe Vivienda Costos (costo por obra y partida). Respeta los filtros globales. */
export function useViviendaCostos() {
  const { filtros } = useFiltrosGlobales();
  return useQuery({
    queryKey: ['vivienda-costos', filtros],
    queryFn: () => cliente.avance.viviendaCostos(filtros),
    placeholderData: keepPreviousData,
  });
}

/** Informe Costo por cuadrilla (M.O. real repartida por horas). Respeta los filtros globales. */
export function useViviendaCuadrillas() {
  const { filtros } = useFiltrosGlobales();
  return useQuery({
    queryKey: ['vivienda-cuadrillas', filtros],
    queryFn: () => cliente.avance.viviendaCuadrillas(filtros),
    placeholderData: keepPreviousData,
  });
}

/** Alerta de producción: BC asentó distinto a las líneas de producción registradas. */
export function useAlertasProduccion() {
  return useQuery({
    queryKey: ['alertas-produccion'],
    queryFn: () => cliente.avance.alertasProduccion(),
    staleTime: 5 * 60_000,
  });
}

/**
 * Versión SEMANAL de Venta/Indirectos (desglose Año › Mes › Semana). El tiempo
 * lo controla el filtro global de Períodos (los días marcados se traducen a su
 * semana ISO en el API). Solo se dispara cuando `activo`.
 */
export function useMensualObrasSemanal(activo: boolean) {
  const { filtros } = useFiltrosGlobales();
  return useQuery({
    queryKey: ['mensual-obras-semanal', filtros],
    queryFn: () => cliente.avance.mensualObras(filtros, 'semana'),
    enabled: activo,
  });
}

/**
 * Corte = mes máximo seleccionado en el filtro Períodos global (aniosSel /
 * mesesSel / diasSel). El reporte acumula HASTA ese mes. Si no hay nada
 * seleccionado → null (al día). Comparte el filtro agrupado superior en vez de
 * un selector propio.
 */
function corteDesdeFiltros(f: {
  aniosSel?: number[];
  mesesSel?: string[];
  diasSel?: string[];
}): { anio: number; mes: number } | null {
  let maxYm = 0;
  for (const a of f.aniosSel ?? []) maxYm = Math.max(maxYm, a * 100 + 12); // año entero → dic
  for (const m of f.mesesSel ?? []) {
    const [y, mm] = m.split('-').map(Number);
    if (y && mm) maxYm = Math.max(maxYm, y * 100 + mm);
  }
  for (const d of f.diasSel ?? []) {
    const [y, mm] = d.split('-').map(Number);
    if (y && mm) maxYm = Math.max(maxYm, y * 100 + mm);
  }
  if (maxYm === 0) return null;
  return { anio: Math.floor(maxYm / 100), mes: maxYm % 100 };
}

/** Pantalla Productividad: métricas por obra (General + M.O.). Corte = mes
 *  máximo del filtro Períodos global. `partidaSel` es un filtro LOCAL del reporte
 *  (jerárquico Tarea Padre → Partida) que restringe costo/presupuesto a esa partida. */
export function useProductividad(partidaSel?: PartidaSel) {
  const { filtros } = useFiltrosGlobales();
  const corte = corteDesdeFiltros(filtros);
  return useQuery({
    queryKey: ['productividad', filtros, corte ?? 'actual', partidaSel ?? 'todas'],
    queryFn: () => cliente.avance.productividad(filtros, corte, partidaSel),
    placeholderData: keepPreviousData,
  });
}

/** Matriz Costo/m² × Partida (viviendas, tareas padre 1-4). Corte = mes máximo
 *  del filtro Períodos. Respeta filtros globales. */
export function useCostoM2Matriz() {
  const { filtros } = useFiltrosGlobales();
  const corte = corteDesdeFiltros(filtros);
  return useQuery({
    queryKey: ['costo-m2-matriz', filtros, corte ?? 'actual'],
    queryFn: () => cliente.avance.costoM2Matriz(filtros, corte),
    placeholderData: keepPreviousData,
  });
}

/** Catálogo de partidas para el selector jerárquico de Costos/m². Respeta los
 *  filtros globales (obra/área/proyecto/bloque). */
export function usePartidasCatalogo() {
  const { filtros } = useFiltrosGlobales();
  return useQuery({
    queryKey: ['partidas-catalogo', filtros],
    queryFn: () => cliente.partidas.catalogo(filtros),
    staleTime: 1000 * 60 * 5,
  });
}

/** Modal: Dim.Códigos consumidos en una partida. */
export function usePartidaDetalle(worksNo: string | null, taskNo: string | null) {
  return useQuery({
    queryKey: ['partida-detalle', worksNo, taskNo],
    queryFn: () => cliente.avance.partidaDetalle(worksNo!, taskNo!),
    enabled: !!worksNo && !!taskNo,
    staleTime: 60_000,
  });
}
